import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import toast from 'react-hot-toast'

/**
 * Standardized error response format
 */
export interface ApiError {
  success: boolean
  error: {
    code: string       // e.g., "NOT_FOUND", "VALIDATION_ERROR", "UNAUTHORIZED"
    message: string    // Human-readable message
    details: any       // Optional details (validation errors, etc.)
  }
}

/**
 * Extract a readable error message from various error shapes
 */
function extractErrorMessage(error: AxiosError<ApiError>): string {
  if (error.response?.data?.error?.message) {
    return error.response.data.error.message
  }
  if (error.response?.data?.error) {
    return typeof error.response.data.error === 'string'
      ? error.response.data.error
      : error.response.data.error.message || 'An unexpected error occurred'
  }
  if (error.code === 'ERR_NETWORK') {
    return 'Network error. Please check your connection.'
  }
  if (error.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.'
  }
  return error.message || 'An unexpected error occurred'
}

/**
 * Extract error code from response
 */
function extractErrorCode(error: AxiosError<ApiError>): string {
  if (error.response?.data?.error?.code) {
    return error.response.data.error.code
  }
  if (error.response?.status) {
    const statusCodes: Record<number, string> = {
      400: 'VALIDATION_ERROR',
      401: 'UNAUTHORIZED',
      403: 'FORBIDDEN',
      404: 'NOT_FOUND',
      409: 'CONFLICT',
      422: 'VALIDATION_ERROR',
      429: 'RATE_LIMITED',
      500: 'INTERNAL_ERROR',
      502: 'BAD_GATEWAY',
      503: 'SERVICE_UNAVAILABLE',
    }
    return statusCodes[error.response.status] || 'UNKNOWN_ERROR'
  }
  return 'NETWORK_ERROR'
}

/**
 * Determine toast style based on status code
 */
function getToastStyle(status?: number): 'error' | 'warning' {
  if (status && status >= 500) return 'error'
  return 'warning'
}

// ---------------------------------------------------------------------------
// Invalid / expired token handling
// ---------------------------------------------------------------------------

/** Guard flag to avoid several simultaneous redirects. */
let isRedirectingToLogin = false

/**
 * Returns true when the error is caused by an invalid, expired or revoked token.
 *
 * The API gateway replies with HTTP 403 (`Token invalide` / `Token révoqué`)
 * for those cases, while a missing session yields HTTP 401, so both statuses
 * are handled here.
 */
export function isUnauthorizedError(error: any): boolean {
  const status = error?.response?.status
  if (status === 401) return true
  if (status !== 403) return false

  const data = error?.response?.data
  const message =
    typeof data === 'string' ? data : String(data?.error ?? data?.message ?? '')
  return /token/i.test(message)
}

/**
 * Clears the stored session and redirects to the login page.
 * Safe to call multiple times: performs at most one redirect per page load and
 * never redirects while already on the login page (avoids redirect loops).
 */
export function handleUnauthorized(): void {
  if (typeof window === 'undefined') return
  if (isRedirectingToLogin) return

  localStorage.removeItem('access_token')
  localStorage.removeItem('user')

  if (window.location.pathname === '/login') return

  isRedirectingToLogin = true
  window.location.href = '/login'
}

/**
 * Attaches an unauthorized handler to a specific axios instance so that an
 * invalid/expired/revoked token (401 or the gateway's 403) consistently
 * redirects the user to the login page.
 */
export function registerUnauthorizedInterceptor(instance: any): void {
  if (!instance || typeof instance.interceptors?.response?.use !== 'function') return

  instance.interceptors.response.use(
    (response: any) => response,
    (error: any) => {
      if (isUnauthorizedError(error)) {
        handleUnauthorized()
      }
      return Promise.reject(error)
    }
  )
}

/**
 * Axios response interceptor that:
 * 1. Catches all HTTP errors (4xx, 5xx)
 * 2. Transforms them into standardized ApiError format
 * 3. Shows error toast notification
 * 4. Returns rejected promise with structured error
 */
export function setupErrorInterceptor(): void {
  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError<ApiError>) => {
      const status = error.response?.status
      const errorCode = extractErrorCode(error)
      const errorMessage = extractErrorMessage(error)
      const unauthorized = isUnauthorizedError(error)

      // Build standardized error
      const apiError: ApiError = {
        success: false,
        error: {
          code: errorCode,
          message: errorMessage,
          details: error.response?.data?.error?.details || null,
        },
      }

      // Redirect to the login page when the token is invalid/expired/revoked
      if (unauthorized) {
        handleUnauthorized()
      }

      // Show toast notification (skip when the error is an authentication failure)
      if (!unauthorized) {
        const toastStyle = getToastStyle(status)
        if (toastStyle === 'error') {
          toast.error(errorMessage, { duration: 5000 })
        } else {
          toast(errorMessage, {
            duration: 5000,
            icon: '⚠️',
            style: {
              background: '#FFF3CD',
              color: '#856404',
              border: '1px solid #FFEAA7',
            },
          })
        }
      }

      // Log error details to console in development
      if (import.meta.env.DEV) {
        console.groupCollapsed(`🌐 API Error [${status}] ${errorCode}`)
        console.error('URL:', error.config?.url)
        console.error('Message:', errorMessage)
        console.error('Details:', apiError.error.details)
        console.groupEnd()
      }

      return Promise.reject(apiError)
    }
  )
}

/**
 * Helper to attach the auth token to requests
 */
export function setupAuthInterceptor(): void {
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem('access_token')
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => Promise.reject(error)
  )
}

export default { setupErrorInterceptor, setupAuthInterceptor }
